/**
 * @module view
 *
 * Functional web components. Create custom elements from pure render functions.
 * Inspired by Haunted <https://github.com/matthewp/haunted>
 *
 * @example
 * ```typescript
 * import { html, css } from "lit";
 * import { component } from "./view.ts";
 *
 * type Model = { count: number };
 * type Msg = { type: "increment" } | { type: "decrement" };
 *
 * const CounterView = (state: Model | undefined, send: (msg: Msg) => void) => html`
 *   <button @click="${() => send({ type: "increment" })}">
 *     Count: ${state?.count ?? 0}
 *   </button>
 * `;
 * CounterView.styles = [css`:host { display: block; }`];
 *
 * customElements.define("my-counter", component(CounterView));
 *
 * // Usage: <my-counter .state="${state}" .send="${send}"></my-counter>
 * ```
 */
import {
  CSSResultOrNative,
  LitElement,
  type PropertyValues,
  ReactiveElement,
  type TemplateResult,
} from "lit";
import { property } from "lit/decorators.js";

/**
 * Base class that allows you to check if you're in the first update
 * Useful for creating imperative components that perform one-time setup work.
 */
export class ImperativeElement extends ReactiveElement {
  #isFirstUpdate = true;

  get isFirstUpdate(): boolean {
    return this.#isFirstUpdate;
  }

  protected firstUpdate(_changedProperties: PropertyValues<this>): void {}

  protected override update(changedProperties: PropertyValues<this>): void {
    if (this.#isFirstUpdate) {
      this.firstUpdate(changedProperties);
    }
    super.update(changedProperties);
  }

  protected override firstUpdated(
    _changedProperties: PropertyValues<this>,
  ): void {
    this.#isFirstUpdate = false;
  }
}

export type Send<Action> = (action: Action) => void;

export const sendNowhere = <T>(_action: T): void => {};

export type RenderFn<Model, Action> = (
  state: Model | undefined,
  send: (action: Action) => void,
) => TemplateResult;

export type ViewConfig = {
  styles?: CSSResultOrNative[];
};

export type ViewFn<Model, Action> = RenderFn<Model, Action> & ViewConfig;

export type ZeroArgConstructor<T> = new () => T;

export interface ViewElement<Model, Action> extends LitElement {
  state: Model | undefined;
  send: (action: Action) => void;
}

/* Transform a view function into a custom element class that you can register */
export const component = <Model, Action>(
  fn: ViewFn<Model, Action>,
): ZeroArgConstructor<ViewElement<Model, Action>> => {
  class CustomViewElement extends LitElement {
    static override styles = fn.styles ?? [];

    @property({ type: Object })
    accessor state: Model | undefined = undefined;

    /**
     * Delegate for sending messages up to parent.
     * Assign a callback to this field to pass messages up from component.
     */
    send = sendNowhere<Action>;

    override render(): TemplateResult {
      return fn(this.state, this.send);
    }
  }

  return CustomViewElement;
};
